class PbaProductSearch extends HTMLElement {
  connectedCallback() {
    if (this.input) return;
    this.input = this.querySelector('input[type="search"]');
    this.panel = this.querySelector('[data-suggestions]');
    this.status = this.querySelector('[data-search-status]');
    this.cache = new Map();
    this.requestId = 0;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-controls', `${this.input.id}-results`);
    this.input.addEventListener('input', () => this.schedule());
    this.input.addEventListener('focus', () => this.schedule());
    this.input.addEventListener('keydown', event => this.onKeydown(event));
    this.input.addEventListener('compositionstart', () => { this.composing = true; this.close(); });
    this.input.addEventListener('compositionend', () => { this.composing = false; this.schedule(); });
    this.querySelector('form').addEventListener('submit', event => {
      if (!this.input.value.trim()) event.preventDefault();
      this.close();
    });
    this.addEventListener('focusout', event => {
      if (!this.contains(event.relatedTarget)) this.close();
    });
    this.onOutside = event => { if (!this.contains(event.target)) this.close(); };
    document.addEventListener('pointerdown', this.onOutside);
  }

  disconnectedCallback() {
    this.close();
    document.removeEventListener('pointerdown', this.onOutside);
  }

  schedule() {
    this.close();
    const term = this.input.value.trim();
    if (term.length < 2 || this.composing) return;
    this.timer = setTimeout(() => this.search(term), 250);
  }

  async search(term) {
    const requestId = ++this.requestId;
    this.controller = new AbortController();
    this.status.textContent = this.dataset.loading;
    try {
      let markup = this.cache.get(term);
      if (!markup) {
        const url = new URL(this.dataset.url, window.location.origin);
        url.search = new URLSearchParams({
          q: term,
          'resources[type]': 'product,collection',
          'resources[limit]': '4',
          'resources[limit_scope]': 'each',
          'resources[options][unavailable_products]': 'last',
          section_id: 'pba-search-suggestions'
        });
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error('Search unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[data-pba-search-results]');
        if (!results) throw new Error('Search unavailable');
        markup = results.outerHTML;
        if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
        this.cache.set(term, markup);
      }
      if (requestId !== this.requestId || term !== this.input.value.trim() || document.activeElement !== this.input) return;
      this.panel.innerHTML = markup;
      this.panel.querySelector('[role="listbox"]').id = `${this.input.id}-results`;
      this.options = Array.from(this.panel.querySelectorAll('[role="option"]'));
      this.options.forEach((option, index) => { option.id = `${this.input.id}-option-${index}`; });
      this.activeIndex = -1;
      const availableHeight = (window.visualViewport?.height || window.innerHeight) - this.input.getBoundingClientRect().bottom - 20;
      this.panel.style.maxHeight = `${Math.max(120, Math.min(480, availableHeight))}px`;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = this.panel.querySelector('[data-summary]').dataset.summary;
    } catch (error) {
      if (error.name !== 'AbortError' && requestId === this.requestId) this.status.textContent = this.dataset.error;
    }
  }

  onKeydown(event) {
    if (event.isComposing) return;
    if (event.key === 'Escape') {
      if (!this.panel.hidden) event.preventDefault();
      this.close();
      return;
    }
    if (this.panel.hidden || !this.options?.length) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = this.activeIndex < 0 ? (direction > 0 ? 0 : this.options.length - 1) : (this.activeIndex + direction + this.options.length) % this.options.length;
      this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
      const option = this.options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', option.id);
      option.scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.activeIndex >= 0) {
      event.preventDefault();
      this.options[this.activeIndex].click();
      this.close();
    }
  }

  close() {
    clearTimeout(this.timer);
    this.controller?.abort();
    this.requestId++;
    if (!this.input) return;
    this.panel.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
    this.status.textContent = '';
  }
}

if (!customElements.get('pba-product-search')) customElements.define('pba-product-search', PbaProductSearch);
